// Structured COIN Search state, API boundary, summaries, and analytics.
import ReactGA4 from 'react-ga4';
import { buildTrackedEbaySearchUrl } from './ebayUtils';

export const QUICK_FILTERS = [
  ['rookie', 'Rookie'], ['auto', 'Autograph'], ['parallel', 'Parallel'], ['graded', 'Graded Only'],
  ['rawOnly', 'Raw Only'], ['misspellings', 'Include Misspellings'], ['psa10', 'PSA 10']
];
// Only one grading requirement can apply to a search at a time.
export const GRADE_FILTERS = ['graded', 'rawOnly', 'psa10'];
export const SORT_OPTIONS = [
  ['best', 'Best Match'], ['newest', 'Newly Listed'], ['ending', 'Ending Soonest'],
  ['lowest', 'Price + Shipping: Lowest'], ['highest', 'Price + Shipping: Highest']
];
// Curated pool for the Typo Hunter's rotating example hint -- a fresh trio is
// sampled on each mount so the same three examples don't get stale.
const TYPO_HUNTER_EXAMPLES = [
  'Wembanyama', 'Topps Chrome', 'PSA 10', 'Charizard', 'Shohei Ohtani',
  'Bowman Chrome', 'Downtown', 'Prizm', 'Caitlin Clark', 'Refractor',
  '1st Bowman', 'Optic', 'Mahomes', 'Pokemon 151'
];
export const randomTypoExamples = (count = 3) => {
  const pool = [...TYPO_HUNTER_EXAMPLES];
  const picked = [];
  while (picked.length < count && pool.length) {
    picked.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  }
  return picked;
};
export const INITIAL_BUILDER = {
  players: '', sets: '', include: '', exclude: '', filters: [],
  format: '', priceMin: '', priceMax: '', sold: false, sort: 'best'
};
// Commas separate entries; spaces, slashes, apostrophes and hyphens stay literal.
export const splitTerms = value => value.split(',').map(term => term.trim()).filter(Boolean);
// Typo Hunter suggestions the user removed; omitted when empty so untouched
// searches keep their original shape.
export const TYPO_LISTING_TYPES = [['', 'Any'], ['bin', 'Buy It Now'], ['auction', 'Auction']];
const LISTING_TYPE_LABELS = { bin: 'Buy It Now', auction: 'Auction' };
export const searchSelections = (mode, player, builder, removedVariants = [], typoFormat = '') => mode === 'typo'
  ? { mode, players: [player.trim()].filter(Boolean),
      ...(removedVariants.length ? { removedVariants: [...removedVariants] } : {}),
      ...(typoFormat ? { format: typoFormat } : {}) }
  : {
      mode, players: splitTerms(builder.players), sets: splitTerms(builder.sets),
      include: splitTerms(builder.include), exclude: splitTerms(builder.exclude),
      cardTypes: builder.filters.filter(filter => !['rawOnly', 'misspellings'].includes(filter)),
      rawOnly: builder.filters.includes('rawOnly'), misspellings: builder.filters.includes('misspellings'),
      format: builder.format || null, sold: builder.sold, sort: builder.sort,
      priceMin: builder.priceMin || null, priceMax: builder.priceMax || null
    };

// Conservative transport guard: live eBay requests around 7.8 KB failed.
// This applies to the complete encoded affiliate URL, not keywords/OR groups.
// It is not a claimed universal eBay limit. See docs/coin-search-feedback.md.
export const MAX_SEARCH_URL_LENGTH = 7800;
const requestSearch = async (selections, signal) => {
  const response = await fetch('/api/search/build', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(selections), signal
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Unable to build this search.');
  return { ...data, url: buildTrackedEbaySearchUrl(data, selections.mode), selections };
};

export const compileSearch = async (selections, signal) => {
  let result = await requestSearch(selections, signal);
  if (result.url.length > MAX_SEARCH_URL_LENGTH && selections.mode === 'builder' && selections.misspellings) {
    result = await requestSearch({ ...selections, misspellings: false }, signal);
    result = { ...result, selections, truncated: true, warnings: [...(result.warnings || []),
      'This link is too long with added misspellings. Your original entries and filters are kept.'] };
  }
  if (result.url.length > MAX_SEARCH_URL_LENGTH) {
    throw new Error('This search link is too long to open reliably. Remove one or more entries to continue.');
  }
  return result;
};

export const searchSummary = selections => {
  if (!selections?.players.length) return ['Enter a name or keyword to start your search.'];
  const filters = QUICK_FILTERS.filter(([key]) => selections.cardTypes?.includes(key) || selections[key]).map(([, label]) => label);
  const lines = [selections.players.join(' · '), selections.sets?.join(' · '), filters.join(' · ')];
  if (selections.mode === 'typo') lines.push('Misspelling search');
  if (selections.mode === 'typo' && selections.format) lines.push(LISTING_TYPE_LABELS[selections.format]);
  if (selections.removedVariants?.length) lines.push(`Removed suggestions: ${selections.removedVariants.join(' · ')}`);
  if (selections.include?.length) lines.push(`Include any of: ${selections.include.join(' · ')}`);
  if (selections.exclude?.length) lines.push(`Exclude: ${selections.exclude.join(' · ')}`);
  if (selections.priceMin != null || selections.priceMax != null) {
    lines.push(selections.priceMin != null && selections.priceMax != null
      ? `$${selections.priceMin}–$${selections.priceMax}`
      : selections.priceMin != null ? `From $${selections.priceMin}` : `Up to $${selections.priceMax}`);
  }
  if (selections.mode === 'builder') lines.push([
    LISTING_TYPE_LABELS[selections.format] || 'Any listing type',
    selections.sold ? 'Sold Listings' : '',
    SORT_OPTIONS.find(([value]) => value === selections.sort)?.[1]
  ].filter(Boolean).join(' · '));
  return lines.filter(Boolean);
};

export const trackSearchExecution = result => {
  const state = result.selections;
  const eventId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  const fields = {
    event_id: eventId, tool: state.mode, source: 'manual',
    smart_filters: QUICK_FILTERS.filter(([key]) => state.cardTypes?.includes(key) || state[key]).map(([key]) => key).join(','),
    players: state.players.join(', '), products: state.sets?.join(', ') || '',
    variant_count: result.variants?.length || 0
  };
  // A build/debounce/copy is not a search execution. One deliberate navigation
  // emits one execution and one outbound event with the same interaction ID.
  try {
    ReactGA4.event('qf_search_executed', fields);
    ReactGA4.event('qf_ebay_click', fields);
  } catch (_) { /* Analytics must never prevent navigation. */ }
  fetch('/api/search/track', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ tool: state.mode, query: result.query, players: state.players,
      rawOnly: state.rawOnly, variantCount: result.variants?.length || 0,
      selections: state, source: 'manual', eventId })
  }).catch(() => {});
};

// --- Saved searches -------------------------------------------------------
// Inverse of searchSelections: rebuild editable form state from a stored search
// so a re-run lands in the same tool with the same inputs the user saved.
export const builderFromSelections = selections => ({
  players: (selections.players || []).join(', '),
  sets: (selections.sets || []).join(', '),
  include: (selections.include || []).join(', '),
  exclude: (selections.exclude || []).join(', '),
  filters: [
    ...(selections.cardTypes || []),
    ...(selections.rawOnly ? ['rawOnly'] : []),
    ...(selections.misspellings ? ['misspellings'] : [])
  ],
  format: selections.format || '',
  priceMin: selections.priceMin || '',
  priceMax: selections.priceMax || '',
  sold: Boolean(selections.sold),
  sort: selections.sort || 'best'
});

export const defaultSearchName = selections => {
  const parts = [
    (selections.players || []).join(', '),
    (selections.sets || []).join(', ')
  ].filter(Boolean);
  const base = parts.join(' — ') || 'Untitled search';
  const label = selections.mode === 'typo' ? `${base} (typos)` : base;
  return label.slice(0, 120);
};

export const listSavedSearches = async () => {
  const response = await fetch('/api/search/saved', { credentials: 'include' });
  if (response.status === 401) return null; // Signed out; not an error to show.
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Unable to load saved searches.');
  return data.searches || [];
};

export const saveSearch = async (name, selections, { createNew = false } = {}) => {
  const response = await fetch('/api/search/saved', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, selections, ...(createNew ? { create_new: true } : {}) })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Unable to save this search.');
  return data;
};

export const updateSavedSearch = async (id, name, selections) => {
  const response = await fetch(`/api/search/saved/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, selections })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Unable to update this search.');
  return data;
};

export const deleteSavedSearch = async id => {
  const response = await fetch(`/api/search/saved/${id}`, {
    method: 'DELETE', credentials: 'include'
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Unable to delete this search.');
  }
};
